import { omitBy } from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import pathfinder from '@fiverr-private/pathfinder';
import { SOURCES } from '../../monitoring';
import { tryInvoke } from '../tryInvoke';
import { parseActiveArrayFilters, parseActiveRefFilters, buildActiveFilters } from './urlActiveFilters';

export const UrlContext = {
    /** @type {(url?: string) => import('./types').ContextualParameters} */
    parseQueryParams: (url) => {
        const { queryParameters = {} } = getContext();
        const params = url ? tryInvoke(() => Object.fromEntries(new URL(url).searchParams), {}) : queryParameters;

        return params;
    },

    /** @type {typeof parseActiveArrayFilters} */
    parseActiveArrayFilters: (values = '') => tryInvoke(() => parseActiveArrayFilters(values), []),

    /** @type {typeof buildActiveFilters} */
    buildActiveFilters: (filters = {}) => tryInvoke(() => buildActiveFilters(filters), ''),

    /** @type {() => boolean} */
    isFiverrChoice: () => {
        const isChoice = UrlContext.parseQueryParams().is_choice;
        return isChoice && isChoice !== 'false';
    },

    /** @return {import('./types').ExpertiseParam[]} */
    getUrlSkills: () => {
        const { expertises } = UrlContext.parseQueryParams();
        return tryInvoke(() => UrlContext.parseActiveArrayFilters(expertises), []);
    },

    /** @return {import('./types').GigParam[]} */
    getUrlGigs: () => {
        const { gigs } = UrlContext.parseQueryParams();
        return tryInvoke(() => UrlContext.parseActiveArrayFilters(gigs), []);
    },

    /** @return {Pick<import('./types').GigParam, 'slug'> | undefined} */
    getRefGigSlug: () => {
        const { referrer_gig_slug } = UrlContext.parseQueryParams();
        return referrer_gig_slug ? { slug: referrer_gig_slug } : undefined;
    },

    /** @returns {ReturnType<typeof parseActiveRefFilters>} */
    getRefFilters: () => {
        const { ref } = UrlContext.parseQueryParams();
        return tryInvoke(() => parseActiveRefFilters(ref), {});
    },

    /**
     * @type {(data: {
     *  username: string;
     *  slug: string;
     *  packageId?: number;
     *  extraFast?: boolean;
     *  filteredFactors?: string;
     *  fiverrChoice?: boolean;
     * }) => string}
     */
    buildGigLink: ({ username, slug, packageId, extraFast, filteredFactors, fiverrChoice }) => {
        const { pageCtxId, queryParameters: { ref, imp_id } = {} } = getContext();

        /** @type {Partial<import('./types').ContextualParameters>} */
        const queryParams = omitBy(
            {
                source: SOURCES.page,
                ref_ctx_id: pageCtxId,
                extra_fast: extraFast && String(extraFast),
                pckg_id: packageId && String(packageId),
                fiverr_choice: fiverrChoice && String(fiverrChoice),
                filtered_factors: filteredFactors,
                imp_id,
                ref,
            },
            (value) => !value
        );

        return pathfinder('gig_page', { username, slug }, { query: queryParams });
    },
};
